import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import type { Transaction } from "@/hooks/useTransactions";
import type { CategoryInsight } from "@/domains/analytics/utils/analyticsPresentation";
import { getBudgetMeta, getProgressWidth } from "@/domains/analytics/utils/analyticsPresentation";
import { formatCurrency, formatDate, formatPercent, formatSignedCurrency } from "@/utils/formatters";

type Copy = { primary: string; secondary: string };
type DetailTransaction = Transaction & { amount: number };
type Breakdown = { transactions: DetailTransaction[]; transactionCount: number; topThreeShare: number };
type Props = {
  selectedCategory: CategoryInsight | null;
  onClose: () => void;
  onEditBudget: (category: CategoryInsight) => void;
  getCategoryStatusLabel: (category: CategoryInsight) => string;
  usageCopy: Copy | null;
  overspendCopy: Copy | null;
  driverInsights: string[];
  breakdown: Breakdown | null;
  loadingTransactions: boolean;
};

export const CategoryDetailModal = ({ selectedCategory, onClose, onEditBudget, getCategoryStatusLabel, usageCopy: selectedCategoryUsageCopy, overspendCopy: selectedCategoryOverspendCopy, driverInsights: selectedCategoryDriverInsights, breakdown: selectedCategoryBreakdown, loadingTransactions: loadingCategoryTransactions }: Props) => {
  const { t } = useTranslation("analytics");
  return (
<ModalShell
          isOpen={selectedCategory !== null}
          onClose={onClose}
          labelledBy="analytics-category-detail-title"
          title={selectedCategory?.name ?? ""}
          subtitle={
            selectedCategory ? getCategoryStatusLabel(selectedCategory) : ""
          }
          mobileSheet
          maxWidthClassName="max-w-[720px]"
          panelClassName="!border-[var(--app-border)] !bg-[var(--app-surface)]"
          headerClassName="!bg-[var(--app-surface)] [&_h2]:!text-[var(--app-text)] [&_p]:!text-[var(--app-text-secondary)]"
          contentClassName="bg-[var(--app-surface)]"
          headerMeta={
            selectedCategory ? (
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-[var(--app-neutral-chip-bg)] px-3 py-1 text-xs font-semibold text-[var(--app-text)]">
                  {formatPercent(selectedCategory.percentage)}%{" "}
                  {t("analytics.ofSpend")}
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    getBudgetMeta(selectedCategory.status).chipClass
                  }`}
                >
                  {getCategoryStatusLabel(selectedCategory)}
                </span>
              </div>
            ) : null
          }
        >
          {selectedCategory ? (
            <div className="space-y-5">
              <section
                className="app-panel-soft p-4 sm:p-5"
              >
                <div
                  className={`rounded-[var(--ds-radius-lg)] p-4 ${
                    selectedCategory.status === "over"
                      ? "bg-[var(--app-danger-panel-bg)]"
                      : "app-panel-muted"
                  }`}
                >
                  <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--app-text-secondary)]">
                    {t("analytics.budgetSummaryLabel")}
                  </p>
                  <p
                    className={`mt-2 text-xl font-semibold sm:text-2xl ${
                      selectedCategory.status === "over"
                        ? "text-[var(--app-danger-text)]"
                        : "text-[var(--app-text)]"
                    }`}
                  >
                    {selectedCategoryOverspendCopy?.primary ??
                      selectedCategoryUsageCopy?.primary ??
                      t("analytics.noBudget")}
                  </p>
                  <p className="mt-1 text-sm text-[var(--app-text-secondary)]">
                    {selectedCategoryOverspendCopy?.secondary ??
                      selectedCategoryUsageCopy?.secondary ??
                      t("analytics.noBudgetDetail")}
                  </p>
                </div>

                <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
                  <div>
                    <p className="text-sm text-[var(--app-text-secondary)]">
                      {t("analytics.categoryDetailSubtitle")}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="app-focus-ring min-h-11 w-full rounded-full bg-[var(--app-panel-elevated-bg)] px-4 py-2 text-sm font-semibold text-[var(--app-text)] transition hover:bg-[var(--app-control-hover-bg)] sm:w-auto"
                    onClick={() => onEditBudget(selectedCategory)}
                  >
                    {selectedCategory.budgetAmount
                      ? t("analytics.editBudget")
                      : t("analytics.setBudget")}
                  </button>
                </div>

                {selectedCategory.budgetAmount ? (
                  <div className="mt-4 space-y-3">
                    <div
                      className={`app-meter relative ${
                        selectedCategory.status === "over"
                          ? "ring-1 ring-[var(--app-danger-track-ring)]"
                          : ""
                      }`}
                      style={{
                        background: getBudgetMeta(selectedCategory.status)
                          .track,
                      }}
                    >
                      <div
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{
                          width: getProgressWidth(selectedCategory),
                          background: getBudgetMeta(selectedCategory.status)
                            .accent,
                        }}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(9.5rem,100%),1fr))] gap-3">
                  <div
                    className="app-panel-muted min-w-0 p-3"
                  >
                    <p className="text-xs leading-tight text-[var(--app-text-secondary)]">
                      {t("analytics.amountSpent")}
                    </p>
                    <p className="mt-1 break-words text-base font-semibold tabular-nums text-[var(--app-text)]">
                      {formatCurrency(selectedCategory.spent)}
                    </p>
                  </div>
                  <div
                    className="app-panel-muted min-w-0 p-3"
                  >
                    <p className="text-xs leading-tight text-[var(--app-text-secondary)]">
                      {t("analytics.budgetMetric")}
                    </p>
                    <p className="mt-1 break-words text-base font-semibold tabular-nums text-[var(--app-text)]">
                      {selectedCategory.budgetAmount
                        ? formatCurrency(selectedCategory.budgetAmount)
                        : t("analytics.noBudget")}
                    </p>
                  </div>
                  <div
                    className="app-panel-muted min-w-0 p-3"
                  >
                    <p className="text-xs leading-tight text-[var(--app-text-secondary)]">
                      {t("analytics.remainingBudget")}
                    </p>
                    <p
                      className={`mt-1 break-words text-base font-semibold tabular-nums ${
                        (selectedCategory.remaining ?? 0) < 0
                          ? "text-[var(--app-danger-text)]"
                          : "text-[var(--app-text)]"
                      }`}
                    >
                      {selectedCategory.remaining != null
                        ? formatCurrency(selectedCategory.remaining)
                        : t("analytics.notAvailable")}
                    </p>
                  </div>
                  <div
                    className="app-panel-muted min-w-0 p-3"
                  >
                    <p className="text-xs leading-tight text-[var(--app-text-secondary)]">
                      {t("analytics.overUnderStatus")}
                    </p>
                    <p
                      className={`mt-1 break-words text-base font-semibold ${
                        getBudgetMeta(selectedCategory.status).labelClass
                      }`}
                    >
                      {getCategoryStatusLabel(selectedCategory)}
                    </p>
                  </div>
                </div>
              </section>

              <section
                className="app-panel-soft p-4 sm:p-5"
              >
                <div>
                  <h3 className="text-lg font-semibold leading-tight text-[var(--app-text)] sm:text-xl">
                    {t("analytics.driversTitle")}
                  </h3>
                  <p className="mt-1 text-sm text-[var(--app-text-secondary)]">
                    {t("analytics.driversSubtitle")}
                  </p>
                </div>

                {loadingCategoryTransactions ? (
                  <div
                    className="app-panel-muted mt-3 px-3 py-3 text-sm text-[var(--app-text-secondary)]"
                  >
                    {t("analytics.loadingShort")}
                  </div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {selectedCategoryDriverInsights.map((insight, index) => (
                      <p
                        key={`${index}-${insight}`}
                        className="app-panel-muted px-3 py-3 text-sm text-[var(--app-text)]"
                      >
                        {insight}
                      </p>
                    ))}
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold leading-tight text-[var(--app-text)] sm:text-xl">
                      {t("analytics.transactionsTitle")}
                    </h3>
                    <p className="text-sm text-[var(--app-text-secondary)]">
                      {t("analytics.transactionsSubtitle")}
                    </p>
                  </div>
                  <span className="rounded-full bg-[var(--app-neutral-chip-bg)] px-3 py-1 text-xs font-semibold text-[var(--app-text-secondary)] sm:shrink-0">
                    {selectedCategoryBreakdown?.transactionCount ?? 0}
                  </span>
                </div>

                {loadingCategoryTransactions ? (
                  <div
                    className="app-panel-soft p-4 text-sm text-[var(--app-text-secondary)]"
                  >
                    {t("analytics.loadingShort")}
                  </div>
                ) : selectedCategoryBreakdown &&
                  selectedCategoryBreakdown.transactionCount > 0 ? (
                  <div className="space-y-3">
                    <div className="app-panel-muted p-3">
                      <p className="text-sm font-medium text-[var(--app-text)]">
                        {t("analytics.topChargesShareSummary", {
                          percent: formatPercent(
                            selectedCategoryBreakdown.topThreeShare * 100,
                          ),
                        })}
                      </p>
                    </div>

                    <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
                      {selectedCategoryBreakdown.transactions.map(
                        (transaction, index) => (
                        <div
                          key={transaction.id}
                          className={`rounded-[var(--ds-radius-md)] px-3 py-3 ${
                            index < 3
                              ? "bg-[var(--app-danger-row-bg)]"
                              : "app-panel-muted"
                          }`}
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="min-w-0 truncate text-sm font-semibold text-[var(--app-text)]">
                                  {transaction.name}
                                </p>
                                {index < 3 ? (
                                  <span className="rounded-full bg-[var(--app-danger-chip-bg)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--app-danger-text)]">
                                    {t("analytics.topChargeBadge", {
                                      rank: index + 1,
                                    })}
                                  </span>
                                ) : null}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--app-text-secondary)]">
                                <span>
                                  {formatDate(
                                    transaction.transaction_date,
                                    undefined,
                                    {
                                      day: "numeric",
                                      month: "short",
                                    },
                                  )}
                                </span>
                                {transaction.account ? (
                                  <span>{transaction.account}</span>
                                ) : null}
                                {transaction.description ? (
                                  <span className="truncate">
                                    {transaction.description}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            <p className="shrink-0 text-sm font-semibold tabular-nums text-[var(--app-danger)] sm:text-right">
                              {formatSignedCurrency(-Math.abs(transaction.amount))}
                            </p>
                          </div>
                        </div>
                        ),
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="app-panel-soft p-4">
                    <p className="text-sm font-medium text-[var(--app-text)]">
                      {t("analytics.noTransactionsForCategory")}
                    </p>
                    <p className="mt-1 text-sm text-[var(--app-text-secondary)]">
                      {t("analytics.noTransactionsForCategoryDetail")}
                    </p>
                  </div>
                )}
              </section>
            </div>
          ) : null}
        </ModalShell>
  );
};
